import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import CustomFormInput from "../CustomFormInput";
import { requestHandler } from "../../helper";
import { toast } from "react-toastify";
import { editUser } from "../../controller";
import { UserValidation } from "../../validate/userValidation";
import ErrorFilter from "../../helper/errorFilter";
import SingleError from "../../helper/SingleError";
import { isOrganizationRole } from "../../helper/accountType";
import ProfilePhoto from "./ProfilePhoto";

const MAX_PHOTO_SIZE = 5 * 1024 * 1024;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const LINK_FIELDS = [
  { name: "instagramUrl", label: "Instagram", placeholder: "https://instagram.com/..." },
  { name: "tiktokUrl", label: "TikTok", placeholder: "https://tiktok.com/@..." },
  { name: "youtubeUrl", label: "YouTube", placeholder: "https://youtube.com/..." },
  { name: "alltrailsUrl", label: "AllTrails", placeholder: "https://alltrails.com/..." },
  { name: "onxUrl", label: "onX", placeholder: "https://onxmaps.com/..." },
];

const EMPTY_USER = {
  firstName: "",
  lastName: "",
  userName: "",
  email: "",
  dateOfBirth: "",
  address: "",
  bio: "",
  ownerFirstName: "",
  ownerLastName: "",
  ownerEmail: "",
  ownerDateOfBirth: "",
  instagramUrl: "",
  tiktokUrl: "",
  youtubeUrl: "",
  alltrailsUrl: "",
  onxUrl: "",
  websiteUrl: "",
};

function Section({ title, hint, children }) {
  return (
    <section className="mb-4">
      <div className="border-bottom pb-2 mb-3">
        <h6 className="fs-16 fw-600 mb-0">{title}</h6>
        {hint && <small className="text-light">{hint}</small>}
      </div>
      <Row className="g-3">{children}</Row>
    </section>
  );
}

function UserEditModal({ show, handleClose, selectedUser, setLoading, getUserData }) {
  const [submitCount, setSubmitCount] = useState(0);
  const [errors, setErrors] = useState({});
  const roleName = selectedUser?.role?.name || selectedUser?.role?.role_name || '';
  const isOrganization = isOrganizationRole(roleName);
  const requireField = isOrganization ? ["firstName", "address"] : ["firstName", "lastName", "address"];
  const [user, setUser] = useState(EMPTY_USER);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoError, setPhotoError] = useState("");

  // Owner date of birth is locked once saved, on the owner or (migrated accounts) on the account; the backend ignores a different date
  const ownerDobLocked = Boolean(selectedUser?.owner?.dateOfBirth || selectedUser?.dateOfBirth);

  // Populate local state when selectedUser changes
  useEffect(() => {
    if (selectedUser) {
      setUser({
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        userName: selectedUser.userName || "",
        email: selectedUser.email || "",
        dateOfBirth: toDateInput(selectedUser.dateOfBirth),
        address: selectedUser.address || "",
        bio: selectedUser.bio || "",
        // AO 2.0 (A2): Organization owner details
        ownerFirstName: selectedUser.owner?.firstName || "",
        ownerLastName: selectedUser.owner?.lastName || "",
        ownerEmail: selectedUser.owner?.email || "",
        ownerDateOfBirth: toDateInput(selectedUser.owner?.dateOfBirth || selectedUser.dateOfBirth),
        // AO 2.0 (A4): external links ('' removes a link)
        instagramUrl: selectedUser.externalLinks?.instagram || "",
        tiktokUrl: selectedUser.externalLinks?.tiktok || "",
        youtubeUrl: selectedUser.externalLinks?.youtube || "",
        alltrailsUrl: selectedUser.externalLinks?.alltrails || "",
        onxUrl: selectedUser.externalLinks?.onx || "",
        websiteUrl: selectedUser.externalLinks?.website || "",
      });
      setErrors({});
      setSubmitCount(0);
      setPhotoFile(null);
      setPhotoError("");
    }
  }, [selectedUser]);

  const handlePhotoSelect = (file) => {
    if (!file) {
      setPhotoFile(null);
      setPhotoError("");
      return;
    }
    if (!file.type?.startsWith("image/")) {
      setPhotoError("Please choose an image file");
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      setPhotoError("Image must be 5 MB or smaller");
      return;
    }
    setPhotoError("");
    setPhotoFile(file);
  };

  const validate = (values) => {
    let validationErrors = ErrorFilter(UserValidation(values), requireField);
    if (isOrganization && values.ownerEmail?.trim() && !EMAIL_PATTERN.test(values.ownerEmail.trim())) {
      validationErrors.ownerEmail = "Enter a valid owner email";
    }
    return validationErrors;
  };

  const buildPayload = () => {
    // Fields that are not editable here (username, email) are not sent
    const { userName, email, ownerDateOfBirth, dateOfBirth, ...rest } = user;
    const payload = { ...rest };
    if (isOrganization) {
      if (!ownerDobLocked && ownerDateOfBirth) payload.ownerDateOfBirth = ownerDateOfBirth;
    } else {
      delete payload.ownerFirstName;
      delete payload.ownerLastName;
      delete payload.ownerEmail;
      delete payload.websiteUrl;
      // Only send the date of birth when it was changed
      if (dateOfBirth && dateOfBirth !== toDateInput(selectedUser?.dateOfBirth)) payload.dateOfBirth = dateOfBirth;
    }
    if (!photoFile) return payload;
    // A new photo goes up with the other fields as multipart
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => formData.append(key, value ?? ""));
    formData.append("profile_image", photoFile);
    return formData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitCount((prevCount) => prevCount + 1);
    const validationErrors = validate(user);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      await requestHandler(
        async () => await editUser(buildPayload(), selectedUser?._id),
        setLoading,
        (res) => {
          toast.success(res.message);
          closeModal();
          getUserData(1);
        },
        () => {
          setErrors({});
        }
      );
    }
  };

  const closeModal = () => {
    handleClose();
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...user, [name]: value };
    if (submitCount > 0) {
      setErrors(validate(next));
    }
    setUser(next);
  };

  const field = (name, label, props = {}) => (
    <CustomFormInput
      type="text"
      label={label}
      id={name}
      name={name}
      value={user[name]}
      onChange={handleChange}
      noThemeClass
      {...props}
    />
  );

  return (
    <Modal show={show} onHide={closeModal} centered size="lg">
      <Modal.Header>
        <Modal.Title>{isOrganization ? "Edit Organization" : "Edit User"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ProfilePhoto user={selectedUser} file={photoFile} onSelect={handlePhotoSelect} error={photoError} />
        <form onSubmit={handleSubmit} noValidate id="user-edit-form">
          <Section title={isOrganization ? "Organization details" : "Personal details"}>
            {isOrganization ? (
              <Col md={12}>
                {field("firstName", "Organization Name", { placeholder: "Enter organization name" })}
                <SingleError error={errors?.firstName} />
              </Col>
            ) : (
              <>
                <Col md={6}>
                  {field("firstName", "First Name", { placeholder: "Enter first name" })}
                  <SingleError error={errors?.firstName} />
                </Col>
                <Col md={6}>
                  {field("lastName", "Last Name", { placeholder: "Enter last name" })}
                  <SingleError error={errors?.lastName} />
                </Col>
              </>
            )}
            <Col md={6}>{field("userName", "Username", { disabled: true })}</Col>
            <Col md={6}>{field("email", "Email", { disabled: true })}</Col>
            {!isOrganization && (
              <Col md={6}>
                {field("dateOfBirth", "Date of Birth", { type: "date", max: toDateInput(new Date()) })}
              </Col>
            )}
            <Col md={isOrganization ? 12 : 6}>
              {field("address", "Location", { placeholder: "Enter location" })}
              <SingleError error={errors?.address} />
            </Col>
            <Col md={12}>
              {field("bio", "Bio", { as: "textarea", rows: 3, placeholder: "Enter bio" })}
            </Col>
          </Section>

          {isOrganization && (
            <Section title="Owner details" hint="The person responsible for this organization">
              <Col md={6}>
                {field("ownerFirstName", "Owner First Name", { placeholder: "Enter owner first name" })}
              </Col>
              <Col md={6}>
                {field("ownerLastName", "Owner Last Name", { placeholder: "Enter owner last name" })}
              </Col>
              <Col md={6}>
                {field("ownerEmail", "Owner Email", { placeholder: "Enter owner email" })}
                <SingleError error={errors?.ownerEmail} />
              </Col>
              <Col md={6}>
                {field("ownerDateOfBirth", "Owner Date of Birth", {
                  type: "date",
                  disabled: ownerDobLocked,
                  max: toDateInput(new Date()),
                })}
                {ownerDobLocked && <small className="text-light">Locked once saved</small>}
              </Col>
            </Section>
          )}

          <Section title="External links" hint="Leave a link empty to remove it">
            {LINK_FIELDS.map(({ name, label, placeholder }) => (
              <Col md={6} key={name}>{field(name, `${label} Link`, { placeholder })}</Col>
            ))}
            {isOrganization && (
              <Col md={6}>{field("websiteUrl", "Website Link", { placeholder: "https://..." })}</Col>
            )}
          </Section>
          <div className="d-sm-flex align-items-center gap-sm-4 gap-2">
            <button type="button" onClick={closeModal} className="btn btn-outline-light w-100">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary w-100 mt-sm-0 mt-2">
              Save Changes
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default UserEditModal;
